import { Endpoints } from 'src/constants/endpoints'
import {
  ComposedInvoiceStatus,
  ComposedInvoicesTableFields,
  InvoiceActionVerb,
  PartnerInvoiceStatus,
  PartnerInvoiceType,
  PartnerInvoicesTableFields,
} from './enums'

/**
 * A `Record` over the verb enum rather than a built URL string: adding a verb without an
 * endpoint fails typechecking, and no path is ever concatenated at a call site.
 */
export const INVOICE_ACTION_ENDPOINTS: Record<InvoiceActionVerb, Endpoints> = {
  [InvoiceActionVerb.Cancel]: Endpoints.AdminInvoiceCancel,
  [InvoiceActionVerb.MarkFunded]: Endpoints.AdminInvoiceMarkFunded,
  [InvoiceActionVerb.MarkFundedNoCheck]: Endpoints.AdminInvoiceMarkFundedNoCheck,
  [InvoiceActionVerb.ProcessPayment]: Endpoints.AdminInvoiceProcessPayment,
  [InvoiceActionVerb.MarkRepaid]: Endpoints.AdminInvoiceRepayment,
  [InvoiceActionVerb.RepayFees]: Endpoints.AdminInvoiceFeesRepayment,
  [InvoiceActionVerb.RepayDueInterest]: Endpoints.AdminInvoiceRepayDueInterest,
}

export const invoiceActionLabels: Record<InvoiceActionVerb, string> = {
  [InvoiceActionVerb.Cancel]: 'Cancel invoices',
  [InvoiceActionVerb.MarkFunded]: 'Mark funded',
  [InvoiceActionVerb.MarkFundedNoCheck]: 'Mark funded (no check)',
  [InvoiceActionVerb.ProcessPayment]: 'Process payments',
  [InvoiceActionVerb.MarkRepaid]: 'Mark repaid',
  [InvoiceActionVerb.RepayFees]: 'Repay invoice fees',
  [InvoiceActionVerb.RepayDueInterest]: 'Repay due interest',
}

/**
 * The sentence shown in the confirm step, before the invoice count. Each states what actually
 * changes — never "are you sure?". `{count}` is substituted by the confirm step.
 */
export const invoiceActionConsequences: Record<InvoiceActionVerb, string> = {
  [InvoiceActionVerb.Cancel]:
    'Cancelling stops funding and repayment for {count}. This is the highest-impact action here and cannot be undone.',
  [InvoiceActionVerb.MarkFunded]:
    'This records that funding has been sent for {count}. It cannot be undone.',
  [InvoiceActionVerb.MarkFundedNoCheck]:
    'This marks {count} as funded WITHOUT checking that external funding completed, and forces the loan funding status to Succeeded. Use it only when you have confirmed funding another way.',
  [InvoiceActionVerb.ProcessPayment]:
    'This starts contractor payouts for {count}. Money moves as a result.',
  [InvoiceActionVerb.MarkRepaid]: 'This records repayment of the loan behind {count}.',
  [InvoiceActionVerb.RepayFees]:
    'This repays the outstanding fees on {count}. Only loan invoices whose principal is already settled can do this.',
  [InvoiceActionVerb.RepayDueInterest]:
    "This pays each invoice's base fee against the GigSafe-wide due-interest balance, for {count}. An invoice whose fee exceeds the remaining balance is left untouched and still reports success.",
}

/** Fallback for `resolveErrorMessage` when the backend sends nothing usable. */
export const invoiceActionFallbackMessages: Record<InvoiceActionVerb, string> = {
  [InvoiceActionVerb.Cancel]: 'Failed to cancel invoice',
  [InvoiceActionVerb.MarkFunded]: 'Failed to mark invoice funded',
  [InvoiceActionVerb.MarkFundedNoCheck]: 'Failed to mark invoice funded',
  [InvoiceActionVerb.ProcessPayment]: 'Failed to process payment',
  [InvoiceActionVerb.MarkRepaid]: 'Failed to mark invoice repaid',
  [InvoiceActionVerb.RepayFees]: 'Failed to repay invoice fees',
  [InvoiceActionVerb.RepayDueInterest]: 'Failed to repay due interest',
}

export const partnerInvoiceStatusLabels: Record<PartnerInvoiceStatus, string> = {
  [PartnerInvoiceStatus.Created]: 'Created',
  [PartnerInvoiceStatus.Pending]: 'Pending',
  [PartnerInvoiceStatus.Canceled]: 'Canceled',
  [PartnerInvoiceStatus.Funded]: 'Funded',
  [PartnerInvoiceStatus.RepaymentPending]: 'Repayment pending',
  [PartnerInvoiceStatus.Repaid]: 'Repaid',
}

export const composedInvoiceStatusLabels: Record<ComposedInvoiceStatus, string> = {
  [ComposedInvoiceStatus.Canceled]: 'Canceled',
  [ComposedInvoiceStatus.FundingFailed]: 'Funding failed',
  [ComposedInvoiceStatus.FundedAndPaid]: 'Funded & paid',
  [ComposedInvoiceStatus.Repaying]: 'Repaying',
  [ComposedInvoiceStatus.AwaitingFunding]: 'Awaiting funding',
}

export const partnerInvoiceTypeLabels: Record<PartnerInvoiceType, string> = {
  [PartnerInvoiceType.Wire]: 'Wire',
  [PartnerInvoiceType.Loan]: 'Loan',
  [PartnerInvoiceType.Historical]: 'Historical',
}

export const composedInvoiceTableHeaderTitles: Record<ComposedInvoicesTableFields, string> = {
  [ComposedInvoicesTableFields.invoice]: 'Invoice',
  [ComposedInvoicesTableFields.type]: 'Type',
  [ComposedInvoicesTableFields.status]: 'Status',
  [ComposedInvoicesTableFields.amount]: 'Amount',
  [ComposedInvoicesTableFields.lateFee]: 'Late fee',
  [ComposedInvoicesTableFields.generated]: 'Invoice generated',
  // The actions column is a single overflow button; a header would just add noise.
  [ComposedInvoicesTableFields.actions]: '',
}

export const partnerInvoiceTableHeaderTitles: Record<PartnerInvoicesTableFields, string> = {
  [PartnerInvoicesTableFields.invoice]: 'Invoice ID',
  [PartnerInvoicesTableFields.status]: 'Status',
  [PartnerInvoicesTableFields.type]: 'Type',
  [PartnerInvoicesTableFields.amount]: 'Amount',
  [PartnerInvoicesTableFields.paidAmount]: 'Paid amount',
  [PartnerInvoicesTableFields.invoiceDate]: 'Invoice date',
  [PartnerInvoicesTableFields.dueDate]: 'Due date',
  [PartnerInvoicesTableFields.earningsCount]: 'Earnings',
  [PartnerInvoicesTableFields.actions]: '',
  [PartnerInvoicesTableFields.externalStatus]: 'External status',
  [PartnerInvoicesTableFields.loanFundingStatus]: 'Loan funding status',
  [PartnerInvoicesTableFields.loanFundingErrorMessage]: 'Loan funding error',
  [PartnerInvoicesTableFields.paymentStatus]: 'Payment status',
  [PartnerInvoicesTableFields.paymentErrorMessage]: 'Payment error',
  [PartnerInvoicesTableFields.paymentDate]: 'Payment date',
  [PartnerInvoicesTableFields.payInvoiceOnDate]: 'Pay invoice on',
  [PartnerInvoicesTableFields.loanId]: 'Loan ID',
  [PartnerInvoicesTableFields.loanDueDate]: 'Loan due date',
  [PartnerInvoicesTableFields.loanTotalAmount]: 'Loan total',
  [PartnerInvoicesTableFields.loanBaseFeeAmount]: 'Loan base fee',
  [PartnerInvoicesTableFields.loanTransferFee]: 'Loan transfer fee',
  [PartnerInvoicesTableFields.loanTransferRate]: 'Loan transfer rate',
  [PartnerInvoicesTableFields.wireTransferFee]: 'Wire transfer fee',
  [PartnerInvoicesTableFields.wireTransferRate]: 'Wire transfer rate',
  [PartnerInvoicesTableFields.gigsafeTotalFeeAmount]: 'GigSafe total fee',
  [PartnerInvoicesTableFields.invoiceApprovedByPayor]: 'Approved by payor',
  [PartnerInvoicesTableFields.payorExternalId]: 'Payor external ID',
  [PartnerInvoicesTableFields.payeeExternalId]: 'Payee external ID',
  [PartnerInvoicesTableFields.paymentId]: 'Payment ID',
  [PartnerInvoicesTableFields.description]: 'Description',
}

/**
 * Server-paginated, so the first value is the initial `limit` sent to the API. Kept small:
 * every row expands into a nested table, so a 100-row page would be unusable.
 */
export const invoicePageSizeOptions = [10, 25, 50]

/** Typed by the admin to unlock the Cancel confirm button. Compared case-insensitively. */
export const INVOICE_CANCEL_CONFIRMATION_WORD = 'CANCEL'

export const COMPOSED_INVOICES_GRID_COLUMNS_STORAGE_KEY = 'admin.invoices.composedGridColumns'
export const PARTNER_INVOICES_GRID_COLUMNS_STORAGE_KEY = 'admin.invoices.partnerGridColumns'
